import { getRequestHooks, isUnverifiedEmail401, notifySessionExpired, SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_TITLE, UPDATE_REQUIRED_MESSAGE } from './requestHooks';
import { parseApiError, logApiError } from '../utils/apiErrorHandler';
import {
  getKlipyGifPreviewUrl,
  getKlipyGifFullUrl,
  getKlipyGifKey,
} from '../utils/klipyGifs';
import type { KlipyGif, KlipyGifsResponse } from '../utils/klipyGifs';
export type { KlipyGif, KlipyGifsResponse } from '../utils/klipyGifs';
export {
  getKlipyGifPreviewUrl,
  getKlipyGifFullUrl,
  getKlipyGifKey,
} from '../utils/klipyGifs';

export interface CreateUserRequest {
  uid: string;
  email: string;
  displayName: string;
  username?: string;
  emailVerified?: boolean;
  photoURL?: string;
  dots?: number;
  referralCount?: number;
  dob?: string;
  bio?: string;
  joydropReason?: string;
  referredBy?: string;
  signupSource?: string;
}

export interface CreateUserResponse {
  uid: string;
  id?: string;
  email: string;
  displayName: string;
  username?: string;
  emailVerified: boolean;
  photoURL?: string;
  dots: number;
  referralCount: number;
  dob?: string | Date;
  birthday?: string;
  bio?: string;
  joydropReason?: string;
  thingsThatBringYouJoy?: string;
  referredBy?: string;
  signupSource?: string;
  createdAt: Date | { _seconds: number; _nanoseconds: number };
  updatedAt: Date | { _seconds: number; _nanoseconds: number };


  // Additional fields from API
  firstName?: string;
  lastName?: string;
  disabled?: boolean;
  status?: string;
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: string;


  // Login/Streak tracking
  loginCount?: number;
  currentStreak?: number;
  longestStreak?: number;
  firstLoginDate?: string;
  lastLoginDate?: string;
  lastStreakDate?: string;
  loginHistory?: Array<{ date: string; streakDay: number }>;


  // Joydrops and connections
  numberOfJoydropsGiven?: number;
  numberOfJoydropsReceived?: number;
  numberOfConnections?: number;

  // Connection metadata (when viewing other profiles)
  connectionStatus?: string;
  connectionId?: string;
  connectionRequesterId?: string;
  connectionTargetId?: string;
  connections?: {
    connectionId?: string;
    status?: string;
  };
  connection?: {
    connectionId?: string;
    status?: string;
    id?: string;
  };


  // Rewards
  rewardEligible?: boolean;
  rewardsClaimed?: {
    thirdDayReward?: boolean;
    claimedAt?: string;
  };

  // Notifications
  hasUnreadNotifications?: boolean;

  // Terms of Use acceptance (Apple Guideline 1.2)
  termsAcceptedAt?: string;
  termsVersion?: string;

  // Set by POST /users/google-login: true when this call just created the account.
  isNewUser?: boolean;

  // Firebase metadata
  metadata?: {
    lastSignInTime?: string;
    creationTime?: string;
  };
  providerData?: Array<{
    uid: string;
    displayName: string;
    email: string;
  }>;
  tokensValidAfterTime?: string;
}

interface GetUserResponse {
  data: CreateUserResponse;
}

export interface AuthRegisterResponse {
  uid: string;
  email: string;
  displayName: string;
  dots: number;
  referralCount: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    uid: string;
    email: string;
    displayName: string;
  };
}

export interface PostReply {
  id?: string;
  userID: string;
  comment: string;
  repliedAt: string | { _seconds: number; _nanoseconds: number };
  likesCount: number;
  user?: { uid: string; username?: string; name?: string; photoURL?: string; avatarUrl?: string } | null;
  mediaUrls?: string[];
}

export interface GetRepliesResponse {
  replies: PostReply[];
  total: number;
  hasMore: boolean;
}

export interface PostLikeEntry {
  userID: string;
  likedAt: string | { _seconds: number; _nanoseconds: number };
  user?: { uid: string; username?: string; name?: string; photoURL?: string; avatarUrl?: string } | null;
}
export interface GetLikesResponse {
  likes: PostLikeEntry[];
  total: number;
  hasMore: boolean;
}

export interface ApiResponse<T> {
  statusCode: number;
  status: 'success' | 'error';
  message: string;
  data: T;
}

export interface CommunityJoinRequestItem {
  id: string;
  communityId: string;
  userId: string;
  status: 'pending' | 'approved' | 'denied';
  requestedAt?: string;
  source?: 'direct_join' | 'member_invite' | string;
  invitationId?: string;
  invitedByUserId?: string;
  suppressDenyNotification?: boolean;
  user?: {
    uid?: string;
    username?: string;
    name?: string;
    photoURL?: string;
  } | null;
}

export interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  joydropReceived: boolean;
  joydropLiked: boolean;
  joydropCommented: boolean;
  newFollower: boolean;
  weeklyDigest: boolean;
  marketingEmails: boolean;
}

export interface PostLike {
  userID: string;
  likedAt: string | { _seconds: number; _nanoseconds: number };
  user?: {
    uid: string;
    username?: string;
    name?: string;
    photoURL?: string;
    avatarUrl?: string;
  } | null;
}

export interface GetPostLikesResponse {
  likes: PostLike[];
  total: number;
  hasMore: boolean;
}

export interface PostComment {
  commentId?: string; // returned id
  id?: string;        // sometimes APIs use id or commentId; support both
  userID: string;
  comment: string;
  commentedAt: string | { _seconds: number; _nanoseconds: number };
  user?: {
    uid: string;
    username?: string;
    name?: string;
    photoURL?: string;
    avatarUrl?: string;
  } | null;
  mediaUrls?: string[];
}

export interface GetPostCommentsResponse {
  comments: PostComment[];
  total: number;
  hasMore: boolean;
}

export interface PostData {
  id: string;
  senderID: string;
  receiverID?: string | null;
  receiverIDs?: string[];
  content: string;
  mediaUrls: string[];
  tags: string[];
  commentsCount: number;
  likesCount: number;
  shareCount: number;
  isPublic: boolean;
  dots?: number;
  /** True when the post was edited after creation (from getPost / feed APIs) */
  isEdited?: boolean;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  updatedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  sender?: {
    uid: string;
    username: string;
    name: string;
    photoURL?: string;
  };
  receiver?: {
    uid: string;
    username: string;
    name: string;
    photoURL?: string;
  };
  receivers?: Array<{
    uid: string;
    username: string;
    name: string;
    photoURL?: string;
  }>;
  /** Community post display - when present, show @communityName with community icon */
  communityId?: string;
  communityName?: string;
  communityIconUrl?: string;
  /** Lucide icon name when community uses preset icon (no customIconUrl) */
  communityIconName?: string;
  /** When communityId is set: true = public community (visible on others' profiles), false = private */
  communityIsPublic?: boolean;
}

// ... (include all other interfaces from your original file here) ...
export interface CreatePostDto {
  senderID: string;
  receiverID?: string | null;
  receiverIDs?: string[];
  content: string;
  mediaUrls?: string[];
  tags?: string[];
  isPublic?: boolean;
  communityId?: string;
  dots?: number;
}

export interface User {
  id: string;
  username: string;
  avatar: string;
  name?: string;
}

export interface TenorMediaFormat {
  url: string;
  dims?: number[];
  duration?: number;
  preview?: string;
  size?: number;
}

export interface TenorMediaFormats {
  gif: TenorMediaFormat;
  tinygif: TenorMediaFormat;
}

export interface TenorGif {
  id: string;
  title?: string;
  media_formats: TenorMediaFormats;
}

export interface TenorResponse {
  results: TenorGif[];
  next?: string;
}

export interface SuggestionRequest {
  what: string;
  why: string;
}

export interface SuggestionResponse {
  statusCode: number;
  status: string;
  data: string[];
}

export interface TrackLoginRequest {
  userID: string;
  loginTime: string; // ISO string
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface TrackLoginResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    userID: string;
    loginDate: string;
    currentStreak: number;
    totalLogins: number;
    longestStreak: number;
    isRewardEligible: boolean;
    rewardClaimed: boolean;
    dotsAwarded: number;
    isFirstLogin: boolean;
    isThirdDay: boolean;
    onboardingCompleted: boolean;
  };
}

export interface UserTagEntry {
  name: string;
  count: number;
}

export interface UserTagsResponse {
  totalTags?: number;
  uniqueTags?: number;
  tags?: Array<string | UserTagEntry>;
}

export type ReportType = 'joydrop' | 'comment' | 'user' | 'community';
export type ReportReason =
  | 'inappropriate_content'
  | 'spam'
  | 'harassment'
  | 'hate_speech'
  | 'violence'
  | 'false_information'
  | 'copyright_violation'
  | 'impersonation'
  | 'other';

export interface ReportSubmissionRequest {
  type: ReportType;
  reason: ReportReason;
  targetId: string;
  targetOwnerId: string;
  message?: string;
  contextId?: string;
  subContextId?: string;
}

export interface BlockedUser {
  id: string;
  username?: string;
  displayName?: string;
  photoURL?: string;
  blockedAt?: string | { _seconds: number; _nanoseconds: number };
}

export interface UpdatePostVisibilityRequest {
  postId: string;
  isPublic: boolean;
  userID: string;
}

export interface UpdatePostRequest {
  content?: string;
  mediaUrls?: string[];
  tags?: string[];
  isPublic?: boolean;
}

export interface UpdatePostResponse {
  postId: string;
  post: PostData;
  updatedAt: string;
}

export interface ConnectionRequestPayload {
  targetUserId: string;
  message?: string;
}

export interface ConnectionRequestResponse {
  statusCode: number;
  status: 'success' | 'error';
  message: string;
  connections: {
    connectionId?: string;
    requesterId?: string;
    targetId?: string;
    message?: string;
    status?: 'pending' | 'accepted' | 'rejected';
  };
}

/** Sentinel return when GET `/communities/discover` is 404 (older backends); caller retries `/communities/public`. */
const COMMUNITIES_DISCOVER_NOT_FOUND = Symbol.for('joydrop.api.communitiesDiscover404');

// --- API SERVICE IMPLEMENTATION ---

export class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    if (!this.baseUrl) {
      console.error("CRITICAL: API base URL is not configured. API calls will fail.");
    }
  }


  /**
   * The single, private method for making all API requests.
   * It handles adding the auth token, setting headers, and robust error handling.
   * @param returnOn404 - When provided, a 404 response returns this value instead of throwing (e.g. for getUserById when user hasn't completed onboarding)
   * @param returnOn403 - When provided, a 403 response returns this value instead of throwing (e.g. for private community posts when access is denied)
   * @param returnOn500 - When provided, a 500 response returns this value instead of throwing (e.g. for community members when backend has transient errors)
   */
  private async request<T>(endpoint: string, token?: string | null, options: RequestInit = {}, returnOn404?: T, returnOn403?: T, returnOn500?: T): Promise<T> {
    try {
      return await this.requestOnce<T>(endpoint, token, options, returnOn404, returnOn403, returnOn500);
    } catch (error) {
      const err = error as Error & { code?: string; statusCode?: number };
      // An expired login token: renew it quietly and retry once.
      if (err?.statusCode === 401 && token && !isUnverifiedEmail401(err.message)) {
        const hooks = getRequestHooks();
        const fresh = hooks.refreshToken ? await hooks.refreshToken().catch(() => null) : null;
        if (fresh) {
          try {
            return await this.requestOnce<T>(endpoint, fresh, options, returnOn404, returnOn403, returnOn500);
          } catch (retryError) {
            const r = retryError as Error & { code?: string; statusCode?: number };
            if (r?.statusCode !== 401 || isUnverifiedEmail401(r.message)) throw retryError;
          }
        }
        // Still not accepted: the session really ended. Sign out with a clear message.
        notifySessionExpired();
        const expired = new Error(`${SESSION_EXPIRED_TITLE} ${SESSION_EXPIRED_MESSAGE}`) as Error & { code?: string; statusCode?: number };
        expired.code = 'SESSION_EXPIRED';
        expired.statusCode = 401;
        throw expired;
      }
      throw error;
    }
  }

  /** One attempt. `request` above adds the silent retry on an expired token. */
  private async requestOnce<T>(endpoint: string, token?: string | null, options: RequestInit = {}, returnOn404?: T, returnOn403?: T, returnOn500?: T): Promise<T> {
    if (!this.baseUrl) {
      const error = new Error("API URL is not configured. Cannot make requests.");
      logApiError(error, `API request to ${endpoint}`);
      throw error;
    }

    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(getRequestHooks().extraHeaders?.() ?? {}),
        ...options.headers,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    };

    try {
      const response = await fetch(url, config);

      // This app version is too old for the API: show the update screen.
      if (response.status === 426) {
        const body = await response.json().catch(() => null);
        getRequestHooks().onUpdateRequired?.({
          minVersion: body?.data?.minVersion ?? null,
          storeUrl: body?.data?.storeUrl ?? null,
          platform: body?.data?.platform ?? null,
        });
        const updateErr = new Error(UPDATE_REQUIRED_MESSAGE) as Error & { code?: string; statusCode?: number };
        updateErr.code = 'APP_UPDATE_REQUIRED';
        updateErr.statusCode = 426;
        throw updateErr;
      }

      // Handle 404 as non-fatal for endpoints that expect it (e.g. new user not in backend yet)
      if (response.status === 404 && returnOn404 !== undefined) {
        return returnOn404;
      }

      // Handle 403 as non-fatal for endpoints where access may be denied (e.g. private community posts)
      if (response.status === 403 && returnOn403 !== undefined) {
        return returnOn403;
      }

      // Handle 500 as non-fatal for endpoints that can degrade gracefully (e.g. community members list)
      if (response.status === 500 && returnOn500 !== undefined) {
        return returnOn500;
      }

      const contentType = response.headers.get('content-type');

      // Handle responses that might not have a body (e.g., 204 No Content)
      if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Request failed with status ${response.status}`);
        }
        return { success: true } as T;
      }

      // Read the JSON body only ONCE and store it.
      const data = await response.json();

      // Handle error responses
      if (!response.ok) {
        const status = data.statusCode || response.status;
        // Structured NestJS errors ({ error: { code, message } }) take precedence over
        // top-level `message` so callers can branch on `code` (e.g. BLOCKED).
        const apiErrorCode: string | undefined = data?.error?.code;
        const apiErrorMessage: string | undefined =
          data?.error?.message ??
          (Array.isArray(data.message) ? data.message.join(', ') : data.message);
        // Include status in the message so parseApiError can extract it
        const finalMessage = apiErrorMessage ? `${apiErrorMessage} (status ${status})` : `Request failed with status ${status}`;
        const err = new Error(finalMessage) as Error & { code?: string; statusCode?: number };
        if (apiErrorCode) err.code = apiErrorCode;
        err.statusCode = status;
        throw err;
      }

      // Special handling for 409 status (conflict - already reported)
      if (data.statusCode === 409) {
        return data;
      }
      return data;
    } catch (error) {
      logApiError(error, `API request to ${endpoint}`);
      const parsedError = parseApiError(error);
      let outward = parsedError.message;
      if (
        parsedError.statusCode != null &&
        typeof parsedError.message === 'string' &&
        !/\(status \d{3}\)/i.test(parsedError.message)
      ) {
        outward = `${parsedError.message} (status ${parsedError.statusCode})`;
      }
      const wrapped = new Error(outward) as Error & { code?: string; statusCode?: number };
      // Preserve the structured error code (e.g. BLOCKED) through the outer catch so
      // callers can branch on it without parsing the message string.
      const originalCode = (error as { code?: unknown })?.code;
      if (typeof originalCode === 'string') wrapped.code = originalCode;
      if (parsedError.statusCode != null) wrapped.statusCode = parsedError.statusCode;
      throw wrapped;
    }
  }

  // --- AUTHENTICATED METHODS ---
  // Each method now accepts a `token` and passes it to the private `request` method.

  async createUser(userData: CreateUserRequest, token?: string | null): Promise<ApiResponse<CreateUserResponse>> {
    return this.request<ApiResponse<CreateUserResponse>>('/users', token, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * Unified Google sign-in endpoint: verifies the Firebase ID token (from Google
   * sign-in) server-side and finds-or-creates the Firestore record by email.
   * Returns the existing record on login, or a newly created one (isNewUser: true)
   * on first-time sign-in — no separate profile-setup call needed either way.
   */
  async googleLogin(token: string): Promise<ApiResponse<CreateUserResponse>> {
    return this.request<ApiResponse<CreateUserResponse>>('/users/google-login', token, {
      method: 'POST',
    });
  }

  async generateSuggestions(request: SuggestionRequest, token: string | null): Promise<SuggestionResponse> {
    return this.request<SuggestionResponse>('/ai/suggestions', token, {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }


  async checkEmailAvailability(email: string): Promise<boolean> {
    try {
      const response = await this.request<ApiResponse<{ exists: boolean }>>(`/users/public/check-email/${encodeURIComponent(email)}`, null, { method: 'GET' });
      // Check if the data object is empty (no user) or has properties (user exists)
      return response.data?.exists === true;
    } catch (error) {
      // If we get a 401, we can't check - default to not existing
      console.warn(`Error checking email availability: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  }

  async checkUsernameAvailability(username: string): Promise<boolean> {
    try {
      const response = await this.request<ApiResponse<{ exists: boolean }>>(`/users/public/check-username/${encodeURIComponent(username)}`, null, { method: 'GET' });
      // Check if the data object is empty (no user) or has properties (user exists)
      return response.data?.exists === true;
    } catch (error) {
      console.warn(`Error checking username availability: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  }

  async deleteUser(uid: string, token?: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/users/${uid}`, token, {
      method: 'DELETE',
    });
  }

  async deleteUserAccount(userID: string, token: string | null): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`/users/${userID}/delete`, token, {
      method: 'PATCH',
    });
  }

  /** Registers (or replaces) this device's Expo push token so the backend can
   * send remote push notifications for the same events shown in-app. */
  async updateExpoPushToken(userId: string, expoPushToken: string, token: string | null): Promise<ApiResponse<unknown>> {
    return this.request<ApiResponse<unknown>>(`/users/${userId}/push-token`, token, {
      method: 'PATCH',
      body: JSON.stringify({ expoPushToken }),
    });
  }

  async completeOnboarding(userID: string, token: string | null): Promise<ApiResponse<CreateUserResponse>> {
    return this.request<ApiResponse<CreateUserResponse>>(`/users/onboarding/complete`, token, {
      method: 'POST',
      body: JSON.stringify({ userID }),
    });
  }

  async getUserFollowing(userId: string, params?: {
    limit?: number;
    offset?: number;
  }, token?: string | null) {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());

    const response = await this.request<ApiResponse<User[]>>(`/users/${userId}/following?${query.toString()}`, token, {
      method: "GET",
    });
    return response.data;
  }

  async getUserFollowers(userId: string, params?: {
    limit?: number;
    offset?: number;
  }, token?: string | null) {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());

    const response = await this.request<ApiResponse<User[]>>(`/users/${userId}/followers?${query.toString()}`, token, {
      method: "GET",
    });
    return response.data;
  }

  async followUser(userId: string, token?: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/users/${userId}/follow`, token, {
      method: 'POST',
    });
  }

  async unfollowUser(userId: string, token: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/users/${userId}/unfollow`, token, {
      method: 'DELETE',
    });
  }

  async getUserStats(userId: string, token: string | null) {
    const response = await this.request<ApiResponse<{
      totalPosts: number;
      totalLikes: number;
      totalShares: number;
      totalDots: number;
      joinDate: string;
    }>>(`/users/${userId}/stats`, token, {
      method: "GET",
    });
    return response.data;
  }

  async markAllNotificationsAsRead(token: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>('/notifications/mark-all-read', token, {
      method: 'PATCH',
      body: JSON.stringify({}),
    });
  }

  async markUserNotificationsAsRead(userId: string, token: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/users/${userId}/notifications/read`, token, {
      method: 'PATCH',
    });
  }

  async getUserByEmail(email: string, token?: string | null): Promise<ApiResponse<CreateUserResponse | null>> {
    return this.request<ApiResponse<CreateUserResponse | null>>(`/users/email/${encodeURIComponent(email)}`, token);
  }

  async getUserByUsername(username: string, token?: string | null): Promise<ApiResponse<CreateUserResponse | null>> {
    return this.request<ApiResponse<CreateUserResponse | null>>(`/users/username/${encodeURIComponent(username)}`, token);
  }

  async getUserById(uid: string, token?: string | null): Promise<ApiResponse<CreateUserResponse | null>> {
    return this.request<ApiResponse<CreateUserResponse | null>>(`/users/${uid}`, token, {}, {
      statusCode: 404,
      status: 'error',
      message: 'User not found',
      data: null,
    });
  }

  async getUserList(userId: string, params: {
    limit?: number;
    offset?: number;
  }, token: string | null): Promise<{
    users: Array<{
      userId: string;
      photoURL: string;
      username: string;
      isConnected: boolean;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> {
    try {
      const query = new URLSearchParams();
      query.append('userId', userId);
      if (params.limit) query.append('limit', params.limit.toString());
      if (params.offset) query.append('offset', params.offset.toString());

      const response = await this.request<ApiResponse<{
        users: Array<{
          userId: string;
          photoURL: string;
          username: string;
          isConnected: boolean;
        }>;
        total: number;
        limit: number;
        offset: number;
      }>>(`/users/list?${query.toString()}`, token, {
        method: 'GET',
      });

      return response.data;
    } catch (error) {
      console.error('Failed to fetch user list:', error);
      return {
        users: [],
        total: 0,
        limit: params.limit || 20,
        offset: params.offset || 0
      };
    }
  }

  async searchUsers(query: {
    query?: string;
    username?: string;
    displayName?: string;
    limit?: number;
    offset?: number;
    communityId?: string;
  }, token?: string | null): Promise<ApiResponse<CreateUserResponse[]>> {
    try {
      const params = new URLSearchParams();
      if (query.query) params.append('query', query.query);
      if (query.username) params.append('username', query.username);
      if (query.displayName) params.append('displayName', query.displayName);
      if (query.limit) params.append('limit', query.limit.toString());
      if (query.offset) params.append('offset', query.offset.toString());
      if (query.communityId) params.append('communityId', query.communityId);


      const response = await this.request<ApiResponse<CreateUserResponse[]>>(`/users/search?${params.toString()}`, token, {
        method: 'GET',
      });
      return response;


      // Transform API response to User interface
      // return response.data.map(user => ({
      //   id: user.uid,
      //   username: user.displayName || user.email.split('@')[0],
      //   avatar: user.photoURL || "/PostCard/Avatar_sender.png",
      //   name: user.displayName
      // }));
    } catch (error) {
      console.error('Failed to search users:', error);
      return {
        data: [],
        statusCode: 500,
        status: 'error',
        message: 'Failed to search users'
      };
    }
  }

  async sendConfirmationEmail(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>('/auth/send-confirmation', null, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resendConfirmationEmail(email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>('/auth/resend-confirmation', null, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Global home feed (`GET /posts`). The backend should include private-community joydrops
   * for the authenticated user when they are still a member and are the sender, direct
   * receiver, or included in `receiverIDs` (send-all), consistent with profile/community feeds.
   */
  async getPosts(params: { limit?: number; offset?: number, onlyHighlights: string, userID?: string }, token: string | null) {
    const query = new URLSearchParams();
    if (params.limit) query.append("limit", params.limit.toString());
    if (params.offset) query.append("offset", params.offset.toString());
    if (params.onlyHighlights) query.append("onlyHighlights", params.onlyHighlights.toString());
    if (params.userID) query.append("userID", params.userID.toString());

    const defaultResponse: ApiResponse<{ posts: PostData[]; hasMore: boolean; total: number }> = {
      data: { posts: [], hasMore: false, total: 0 },
      statusCode: 200,
      status: 'success',
      message: 'Backend unavailable',
    };
    return this.request<ApiResponse<{ posts: PostData[], hasMore: boolean, total: number }>>(`/posts?${query.toString()}`, token, {}, defaultResponse);
  }
  /**
   * Single joydrop by ID (`GET /posts/:postId`). Backend enforces visibility:
   * you get the post if you're the sender/receiver, or if it's genuinely
   * public. Otherwise it returns a 404-shaped body (not an HTTP 404).
   */
  async getPostById(postId: string, token: string | null) {
    const defaultResponse: ApiResponse<{ post: PostData | null }> = {
      data: { post: null },
      statusCode: 503,
      status: 'error',
      message: 'Backend unavailable',
    };
    return this.request<ApiResponse<{ post: PostData; posts?: PostData; total: number; hasMore: boolean }>>(
      `/posts/${postId}`,
      token,
      {},
      defaultResponse as any,
    );
  }
  async createPost(postData: CreatePostDto, token: string | null): Promise<ApiResponse<PostData>> {
    return this.request<ApiResponse<PostData>>('/posts', token, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
  }

  async updateUserProfileFields(uid: string, profileData: {
    dob?: string;
    bio?: string;
    joydropReason?: string;
  }, token?: string | null): Promise<ApiResponse<CreateUserResponse>> {
    return this.request<ApiResponse<CreateUserResponse>>(`/users/${uid}`, token, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async updateUserProfile(uid: string, profileData: Partial<CreateUserRequest>, token?: string | null): Promise<ApiResponse<CreateUserResponse>> {
    return this.request<ApiResponse<CreateUserResponse>>(`/users/${uid}`, token, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async uploadUserAvatar(uid: string, formData: FormData, token: string): Promise<ApiResponse<{ photoURL: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/user/${uid}/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      logApiError('uploadUserAvatar', error as string);
      throw parseApiError(error);
    }
  }

  // Generate signed upload URL for photo
  async getPhotoUploadUrl(
    userId: string,
    contentType: string,
    token: string
  ): Promise<ApiResponse<{
    uploadUrl: string;
    filePath: string;
    downloadUrl: string;
    expiresAt: string;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${userId}/photo/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ contentType }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      logApiError('getPhotoUploadUrl', error as string);
      throw parseApiError(error);
    }
  }

  // Confirm photo upload
  async confirmPhotoUpload(
    userId: string,
    filePath: string,
    downloadUrl: string,
    token: string
  ): Promise<ApiResponse<{
    photoURL: string;
    filePath: string;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/users/${userId}/photo/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ filePath, downloadUrl }),
      });

      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        throw new Error('We had trouble confirming your photo upload. Please try again.');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      logApiError('confirmPhotoUpload', error as string);
      throw new Error('We had trouble confirming your photo upload. Please try again.');
    }
  }
  // async getUserbyUsername(uid: string): Promise<ApiResponse<{ message: string }>> {
  //   return this.request<ApiResponse<{ message: string }>>(`/users/username/${uid}`, {
  //     method: 'GET',
  //   });
  // }

  async blockUser(userId: string, token?: string | null, reason?: string): Promise<ApiResponse<{ id: string }>> {
    if (!token) throw new Error('Authentication required to block user');
    return this.request<ApiResponse<{ id: string }>>(`/users/${userId}/block`, token, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? undefined }),
    });
  }

  async unblockUser(userId: string, token?: string | null): Promise<ApiResponse<Record<string, never>>> {
    if (!token) throw new Error('Authentication required to unblock user');
    return this.request<ApiResponse<Record<string, never>>>(`/users/${userId}/block`, token, {
      method: 'DELETE',
    });
  }

  async getBlockedUsers(token?: string | null): Promise<ApiResponse<{ blocked: BlockedUser[] }>> {
    if (!token) throw new Error('Authentication required to list blocked users');
    return this.request<ApiResponse<{ blocked: BlockedUser[] }>>(`/users/me/blocked`, token, {
      method: 'GET',
    });
  }

  async acceptTerms(version: string, token?: string | null): Promise<ApiResponse<{ termsAcceptedAt: string; termsVersion: string }>> {
    if (!token) throw new Error('Authentication required to accept terms');
    return this.request<ApiResponse<{ termsAcceptedAt: string; termsVersion: string }>>(`/users/me/accept-terms`, token, {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  }

  async submitReport(params: ReportSubmissionRequest, token?: string | null): Promise<ApiResponse<{ message: string }>> {
    if (!token) {
      throw new Error('Authentication required to submit report');
    }

    // Backend only accepts type: joydrop | comment | user. Map 'community' to 'user' and report the community admin.
    const body =
      params.type === 'community'
        ? { ...params, type: 'user' as const, targetId: params.targetOwnerId || params.targetId }
        : params;

    return this.request<ApiResponse<{ message: string }>>('/reports', token, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getUserTags(userId: string, token?: string | null): Promise<UserTagsResponse> {
    const response = await this.request<ApiResponse<UserTagsResponse>>(
      `/users/${userId}/tags`,
      token ?? null,
      { method: 'GET' }
    );
    return response.data;
  }

  async getSentPosts(userId: string, params?: {
    limit?: number;
    offset?: number;
  }, token?: string) {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());

    const response: any = await this.request<ApiResponse<PostData[]> & { hasMore: boolean; total: number }>(
      `/posts/sent/${userId}?${query.toString()}`, token,
      {
        method: "GET",
      }
    );

    // Check if response.data has posts property or is the posts array directly
    const posts = response.data?.posts || response.data || [];
    const hasMore = response.data?.hasMore ?? response.hasMore ?? false;
    const total = response.data?.total ?? response.total ?? 0;

    console.log("Processed sent posts:", { posts, hasMore, total });

    return {
      posts,
      hasMore,
      total
    };
  }

  /**
   * Get received posts (joydrops received) by user
   * @param userId User ID
   * @param params Query parameters
   * @param token Authentication token
   * @returns Received posts
   */
  async getReceivedPosts(userId: string, params?: {
    limit?: number;
    offset?: number;
    communityId?: string;
  }, token?: string) {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());
    if (params?.communityId) query.append("communityId", params.communityId);

    const response: any = await this.request<ApiResponse<PostData[]> & { hasMore: boolean; total: number }>(
      `/posts/received/${userId}?${query.toString()}`, token,
      {
        method: "GET",
      }
    );

    console.log("getReceivedPosts response:", response);

    // Check if response.data has posts property or is the posts array directly
    const posts = response.data?.posts || response.data || [];
    const hasMore = response.data?.hasMore ?? response.hasMore ?? false;
    const total = response.data?.total ?? response.total ?? 0;

    console.log("Processed received posts:", { posts, hasMore, total });

    return {
      posts,
      hasMore,
      total
    };
  }



  async searchPosts(keyword: string, params?: {
    limit?: number;
    offset?: number;
    tags?: string[];
    userID?: string;
  }, token?: string | null) {
    const searchParams = new URLSearchParams();
    searchParams.append("keyword", keyword);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());
    if (params?.userID) searchParams.append("userID", params.userID);
    if (params?.tags) {
      params.tags.forEach(tag => searchParams.append("tags", tag));
    }

    const response = await this.request<{
      statusCode: number;
      status: string;
      message: string;
      data: {
        posts: PostData[];
        total: number;
        hasMore: boolean;
        keyword: string;
      };
    }>(
      `/posts/search?${searchParams.toString()}`, token,
      {
        method: "GET",
      }
    );
    return response;
  }

  async getTrendingPosts(params?: {
    limit?: number;
    offset?: number;
    timeframe?: 'day' | 'week' | 'month';
  }, token?: string | null) {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());
    if (params?.timeframe) query.append("timeframe", params.timeframe);

    const response = await this.request<ApiResponse<PostData[]> & { hasMore: boolean; total: number }>(
      `/posts/trending?${query.toString()}`, token,
      {
        method: "GET",
      }
    );
    return {
      posts: response.data,
      hasMore: response.hasMore,
      total: response.total
    };
  }

  async getNotifications(params?: {
    limit?: number;
    offset?: number;
    isRead?: boolean;
  }, token?: string | null): Promise<{
    notifications: Array<{
      id: string;
      userId: string;
      actorId: string;
      type: string;
      title: string;
      message: string;
      isRead: boolean;
      metadata: any;
      createdAt: string;
      actor: {
        uid: string;
        username: string;
        name: string;
        photoURL?: string;
      };
    }>;
    total: number;
    hasMore: boolean;
  }> {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());
    if (params?.isRead !== undefined) query.append("isRead", params.isRead.toString());

    try {
      const response = await this.request<ApiResponse<{
        notifications: Array<{
          id: string;
          userId: string;
          actorId: string;
          type: string;
          title: string;
          message: string;
          isRead: boolean;
          metadata: any;
          createdAt: string;
          actor: {
            uid: string;
            username: string;
            name: string;
            photoURL?: string;
          };
        }>;
        total: number;
        hasMore: boolean;
      }>>(`/notifications?${query.toString()}`, token, {
        method: 'GET',
      });

      return response.data;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return {
        notifications: [],
        total: 0,
        hasMore: false
      };
    }
  }

  async sharePost(postId: string, token?: string | null): Promise<ApiResponse<{ shareCount: number }>> {
    return this.request<ApiResponse<{ shareCount: number }>>(`/posts/${postId}/share`, token, {
      method: 'POST',
    });
  }

  async sharePostByEmail(
    postId: string,
    payload: { toEmail: string; subject: string; message?: string; senderName?: string; senderEmail?: string },
    token?: string | null,
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/posts/${postId}/share-email`, token, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async markNotificationAsRead(notificationId: string, token: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/notifications/${notificationId}`, token, {
      method: 'PATCH',
    });
  }

  async deleteNotification(notificationId: string, token: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/notifications/${notificationId}`, token, {
      method: 'DELETE',
    });
  }

  async updatePostVisibility(params: UpdatePostVisibilityRequest, token: string | null): Promise<ApiResponse<any>> {
    const { postId, ...body } = params;
    return this.request<ApiResponse<any>>(`/posts/${postId}/visibility`, token, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async updatePost(postId: string, data: UpdatePostRequest, token: string | null): Promise<ApiResponse<UpdatePostResponse>> {
    // Backend rejects receiverID and other disallowed properties - only send allowed fields
    const allowedKeys: (keyof UpdatePostRequest)[] = ['content', 'mediaUrls', 'tags', 'isPublic'];
    const body = Object.fromEntries(
      allowedKeys.filter((k) => data[k] !== undefined).map((k) => [k, data[k]])
    );
    return this.request<ApiResponse<UpdatePostResponse>>(`/posts/${postId}`, token, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async likePost(postId: string, token: string | null): Promise<ApiResponse<{ likesCount: number }>> {
    return this.request<ApiResponse<{ likesCount: number }>>(`/posts/${postId}/like`, token, {
      method: 'POST',
    });
  }

  async unlikePost(postId: string, token?: string | null): Promise<ApiResponse<{ likesCount: number }>> {
    return this.request<ApiResponse<{ likesCount: number }>>(`/posts/${postId}/unlike`, token, {
      method: 'DELETE',
    });
  }

  async toggleLikePost(postId: string, userID: string, token: string | null): Promise<ApiResponse<{
    postId: string;
    userID: string;
    isLiked: boolean;
  }>> {
    return this.request<ApiResponse<{
      postId: string;
      userID: string;
      isLiked: boolean;
    }>>(`/posts/${postId}/toggle-like`, token, {
      method: 'POST',
      body: JSON.stringify({ userID }),
    });
  }

  async getPostLikes(
    postId: string,
    token: string | null,
    limit: number = 50,
  ): Promise<ApiResponse<GetPostLikesResponse>> {
    const url = `/posts/${postId}/likes?limit=${encodeURIComponent(String(limit))}`;
    return this.request<ApiResponse<GetPostLikesResponse>>(url, token, {
      method: 'GET',
    });
  }

  /**
   * Add a comment to a post
   */
  async commentOnPost(
    postId: string,
    userID: string,
    comment: string,
    token: string | null,
    data?: { mediaUrls?: string[]; taggedUserIds?: string[] }
  ): Promise<ApiResponse<{
    commentId: string;
    postId: string;
    userID: string;
    comment: string;
    commentedAt: string;
  }>> {
    const body: any = { userID, comment };
    if (data?.mediaUrls && data.mediaUrls.length > 0) {
      body.mediaUrls = data.mediaUrls;
    }
    if (data?.taggedUserIds && data.taggedUserIds.length > 0) {
      body.taggedUserIds = data.taggedUserIds;
    }

    return this.request<ApiResponse<{
      commentId: string;
      postId: string;
      userID: string;
      comment: string;
      commentedAt: string;
    }>>(`/posts/${postId}/comment`, token, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  /**
   * Get comments for a post
   */
  async getPostComments(
    postId: string,
    params?: { limit?: number },
    token?: string | null
  ): Promise<GetPostCommentsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';

    const res = await this.request<ApiResponse<GetPostCommentsResponse>>(
      `/posts/${postId}/comments${qs}`,
      token ?? null,
      { method: 'GET' }
    );

    // Backend returns { statusCode, status, message, data }
    return res.data;
  }

  async getUserConnections(userId: string, token?: string, searchKeywords?: string): Promise<ConnectionRequestResponse[]> {
    const url = searchKeywords ? `/users/${userId}/connections?search=${encodeURIComponent(searchKeywords)}` : `/users/${userId}/connections`;
    const response = await this.request<ApiResponse<ConnectionRequestResponse[]>>(url, token, {
      method: "GET",
    });
    return response.data;
  }

  async getPendingSentConnections(userId: string, token?: string, searchKeywords?: string): Promise<ConnectionRequestResponse[]> {
    const url = searchKeywords ? `/users/${userId}/connections/pending-sent?search=${encodeURIComponent(searchKeywords)}` : `/users/${userId}/connections/pending-sent`;
    const response = await this.request<ApiResponse<ConnectionRequestResponse[]>>(url, token, {
      method: "GET",
    });
    return response.data;
  }

  async getPendingReceivedConnections(userId: string, token?: string, searchKeywords?: string): Promise<ConnectionRequestResponse[]> {
    const url = searchKeywords ? `/users/${userId}/connections/pending-received?search=${encodeURIComponent(searchKeywords)}` : `/users/${userId}/connections/pending-received`;
    const response = await this.request<ApiResponse<ConnectionRequestResponse[]>>(url, token, {
      method: "GET",
    });
    return response.data;
  }

  /**
   * Get connection status between current user and target user
   * @param targetUserId The user ID to check connection status with
   * @param token Authentication token
   * @returns Connection status and connection ID
   */
  async getConnectionStatus(targetUserId: string, token?: string | null): Promise<{
    status?: string;
    connectionId?: string;
  }> {
    try {
      const response = await this.request<ApiResponse<{
        status?: string;
        connectionId?: string;
        connection?: {
          status?: string;
          id?: string;
        };
      }>>(`/users/connections/status/${targetUserId}`, token, {
        method: "GET",
      });

      // Extract connection data from response
      const connectionData = response.data;
      return {
        status: connectionData?.status || connectionData?.connection?.status,
        connectionId: connectionData?.connectionId || connectionData?.connection?.id,
      };
    } catch (error) {
      console.error('Failed to fetch connection status:', error);
      // Return undefined if connection doesn't exist or error occurred
      return {};
    }
  }

  async loginUser(loginData: any): Promise<any> {
    return this.request<any>('/auth/login', null, {
      method: 'POST',
      body: JSON.stringify(loginData),
    });
  }

  async contactUs(params: {
    userId: string;
    email: string;
    subject: string;
    message: string;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/contact', null, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // --- THIRD-PARTY API METHODS (Do not use `this.request`) ---

  private tenorApiKey?: string;
  private tenorClientKey?: string;
  private klipyApiKey?: string;

  setTenorKeys(apiKey: string, clientKey: string) {
    this.tenorApiKey = apiKey;
    this.tenorClientKey = clientKey;
  }

  setKlipyKey(apiKey: string) {
    this.klipyApiKey = apiKey;
  }

  async getKlipyGifs(query?: string, page = 1): Promise<KlipyGifsResponse> {
    const emptyResponse: KlipyGifsResponse = { results: [], currentPage: page, hasNext: false };
    const apiKey = this.klipyApiKey?.trim()
      || (typeof process !== 'undefined'
        ? (process.env.NEXT_PUBLIC_KLIPY_API_KEY?.trim() || process.env.EXPO_PUBLIC_KLIPHY_API_KEY?.trim())
        : undefined);
    if (!apiKey) {
      console.debug('[Info] Klipy API key not configured - GIF picker will be empty');
      return emptyResponse;
    }
    if (!this.klipyApiKey) {
      this.klipyApiKey = apiKey;
    }

    try {
      const endpoint = query ? 'search' : 'trending';
      const url = new URL(`https://api.klipy.com/api/v1/${apiKey}/gifs/${endpoint}`);
      url.searchParams.set('per_page', '20');
      url.searchParams.set('page', String(page));
      url.searchParams.set('locale', 'us');
      url.searchParams.set('content_filter', 'medium');
      if (query) url.searchParams.set('q', query);

      const res = await fetch(url.toString());
      if (!res.ok) {
        console.debug('[Info] Klipy API request failed - GIF picker will be empty:', res.status);
        return emptyResponse;
      }

      const json = await res.json() as {
        result?: boolean;
        data?: {
          data?: KlipyGif[];
          current_page?: number;
          has_next?: boolean;
        };
      };

      if (!json.result || !Array.isArray(json.data?.data)) {
        return emptyResponse;
      }

      const results = json.data.data.filter((item) => item.type !== 'ad' && item.file);
      return {
        results,
        currentPage: json.data.current_page ?? page,
        hasNext: json.data.has_next ?? false,
      };
    } catch (err) {
      console.debug('[Info] Klipy API request failed - GIF picker will be empty:', err);
      return emptyResponse;
    }
  }

  // Klipy-backed, returns Tenor-shaped results so existing pickers keep working.
  // "pos" is treated as a Klipy page number ("2", "3", ...).
  async getGifs(query?: string, pos?: string): Promise<TenorResponse> {
    const emptyResponse: TenorResponse = { results: [] };
    try {
      const page = pos ? Math.max(parseInt(pos, 10) || 1, 1) : 1;
      const klipy = await this.getKlipyGifs(query, page);

      const results: TenorGif[] = klipy.results
        .map((gif) => {
          const full = getKlipyGifFullUrl(gif);
          const preview = getKlipyGifPreviewUrl(gif) ?? full;
          if (!full || !preview) return null;
          return {
            id: getKlipyGifKey(gif),
            title: gif.title,
            media_formats: {
              gif: { url: full },
              tinygif: { url: preview },
            },
          } as TenorGif;
        })
        .filter((g): g is TenorGif => g !== null);

      return {
        results,
        next: klipy.hasNext ? String((klipy.currentPage ?? page) + 1) : undefined,
      };
    } catch (err) {
      console.debug("[Info] GIF request failed - GIF picker will be empty:", err);
      return emptyResponse;
    }
  }

  async getUserPosts(userId: string, params?: {
    limit?: number;
    offset?: number;
  }, token?: string | null) {
    const query = new URLSearchParams();
    if (params?.limit) query.append("limit", params.limit.toString());
    if (params?.offset) query.append("offset", params.offset.toString());

    const response = await this.request<ApiResponse<PostData[]> & { hasMore: boolean; total: number }>(
      `/users/${userId}/posts?${query.toString()}`, token,
      {
        method: "GET"
      }
    );

    return {
      posts: response.data,
      hasMore: response.hasMore,
      total: response.total
    };
  }

  async getUserProfile(userId: string, token?: string | null): Promise<CreateUserResponse> {
    const response = await this.request<ApiResponse<CreateUserResponse>>(`/users/${userId}/profile`, token, {
      method: "GET",
    });
    return response.data;
  }

  async deletePost(postId: string, token?: string | null): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>(`/posts/${postId}`, token, {
      method: 'DELETE',
    });
  }

  async getUserDots(uid: string, token: string | null): Promise<number> {
    try {
      const response = await this.request<ApiResponse<CreateUserResponse>>(`/users/${uid}`, token);
      return response.data.dots || 0;
    } catch (error) {
      console.error('Failed to fetch user dots:', error);
      return 0;
    }
  }

  async verifyEmail(token: string, email: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<ApiResponse<{ message: string }>>('/auth/verify-email', token, {
      method: 'POST',
      body: JSON.stringify({ token, email }),
    });
  }

  async updateUserEmailVerified(uid: string, emailVerified: boolean, token: string | null): Promise<ApiResponse<CreateUserResponse>> {
    return this.request<ApiResponse<CreateUserResponse>>(`/users/${uid}/email-verified`, token, {
      method: 'PATCH',
      body: JSON.stringify({ emailVerified }),
    });
  }

  async updateUserDots(userId: string, points: number, reason: string, token: string | null): Promise<ApiResponse<{ dots: number }>> {
    return this.request<ApiResponse<{ dots: number }>>(`/users/${userId}/points`, token, {
      method: 'PUT',
      body: JSON.stringify({ points, reason }),
    });
  }

  async trackLogin(params: TrackLoginRequest, token: string | null): Promise<TrackLoginResponse> {
    const defaultResponse: TrackLoginResponse = {
      statusCode: 200,
      status: 'success',
      message: 'Backend unavailable',
      data: {
        userID: params.userID,
        loginDate: params.loginTime,
        currentStreak: 0,
        totalLogins: 0,
        longestStreak: 0,
        isRewardEligible: false,
        rewardClaimed: false,
        dotsAwarded: 0,
        isFirstLogin: false,
        isThirdDay: false,
        onboardingCompleted: true,
      },
    };
    return this.request<TrackLoginResponse>('/users/track-login', token, {
      method: 'POST',
      body: JSON.stringify(params),
    }, defaultResponse);
  }

  async changePassword(params: ChangePasswordRequest, token: string | null): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/users/change-password', token, {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
  }

  async getUserNotificationSettings(userId: string, token: string | null) {
    return this.request<ApiResponse<NotificationSettings>>(`/users/${userId}/notifications`, token, {
      method: 'GET',
    });
  }

  async updateUserNotificationSettings(
    userId: string,
    settings: NotificationSettings,
    token: string | null,
  ) {
    return this.request<ApiResponse<NotificationSettings>>(`/users/${userId}/notifications`, token, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
    });
  }
  /**
   * Send a connection request to another user
   */
  async sendConnectionRequest(params: ConnectionRequestPayload, token: string | null): Promise<ConnectionRequestResponse> {
    return this.request<ConnectionRequestResponse>('/users/connections/request', token, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }
  // Get current sprint data
  async getCurrentSprint(token: string) {
    const defaultResponse = { statusCode: 404, data: null };
    return this.request('/joysprint/user/current-streak', token, {
      method: 'GET',
    }, defaultResponse);
  }

  // Claim reward
  async claimReward(rewardType: 'streak' | 'joydrop', joysprintId: string, token: string) {
    return this.request('/joysprint/user/claim-reward', token, {
      method: 'POST',
      body: JSON.stringify({ rewardType, joysprintId }),
    });
  }

  async addReply(postId: string, commentId: string, payload: { userID: string; comment: string; mediaUrls?: string[]; taggedUserIds?: string[] }, token: string | null) {

    const body: any = { userID: payload.userID, comment: payload.comment };
    if (payload.mediaUrls && payload.mediaUrls.length > 0) {
      body.mediaUrls = payload.mediaUrls;
    }
    if (payload.taggedUserIds && payload.taggedUserIds.length > 0) {
      body.taggedUserIds = payload.taggedUserIds;
    }

    return this.request<ApiResponse<{ replyId: string }>>(
      `/posts/${postId}/comments/${commentId}/replies`,
      token,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
    );
  }


  async deleteReply(postId: string, commentId: string, replyId: string, token: string | null) {
    return this.request<ApiResponse<{ message: string }>>(
      `/posts/${postId}/comments/${commentId}/replies/${replyId}`,
      token,
      { method: 'DELETE' }
    );
  }

  async editReply(postId: string, commentId: string, replyId: string, comment: string, token: string | null, mediaUrls?: string[]) {
    return this.request<ApiResponse<{ message: string }>>(
      `/posts/${postId}/comments/${commentId}/replies/${replyId}`,
      token,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment, mediaUrls }) }
    );
  }



  async getReplies(postId: string, commentId: string, token: string | null, limit = 50) {
    return this.request<ApiResponse<GetRepliesResponse>>(
      `/posts/${postId}/comments/${commentId}/replies?limit=${limit}`, token, { method: 'GET' }
    );
  }

  async deleteComment(postId: string, commentId: string, token: string | null) {
    return this.request<ApiResponse<{ message: string }>>(
      `/posts/${postId}/comments/${commentId}`, token, { method: 'DELETE' }
    );
  }

  async editComment(postId: string, commentId: string, comment: string, token: string | null, mediaUrls?: string[]) {
    return this.request<ApiResponse<{ message: string }>>(
      `/posts/${postId}/comments/${commentId}`, token,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comment, mediaUrls }) }
    );
  }

  async toggleLikeComment(postId: string, commentId: string, userID: string, token: string | null) {
    return this.request<ApiResponse<{ isLiked: boolean }>>(
      `/posts/${postId}/comments/${commentId}/toggle-like`, token,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userID }) }
    );
  }

  async getCommentLikes(postId: string, commentId: string, token: string | null, limit = 50) {
    return this.request<ApiResponse<GetLikesResponse>>(
      `/posts/${postId}/comments/${commentId}/likes?limit=${limit}`, token, { method: 'GET' }
    );
  }

  async toggleLikeReply(postId: string, commentId: string, replyId: string, userID: string, token: string | null) {
    return this.request<ApiResponse<{ isLiked: boolean }>>(
      `/posts/${postId}/comments/${commentId}/replies/${replyId}/toggle-like`, token,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userID }) }
    );
  }

  async getReplyLikes(postId: string, commentId: string, replyId: string, token: string | null, limit = 50) {
    return this.request<ApiResponse<GetLikesResponse>>(
      `/posts/${postId}/comments/${commentId}/replies/${replyId}/likes?limit=${limit}`, token, { method: 'GET' }
    );
  }


  //   async claimReward(rewardType: 'streak' | 'joydrop', joysprintId: string, token: string) {
  //     return this.request('/joysprint/user/claim-reward', token, {
  //       method: 'POST',
  //       body: JSON.stringify({ rewardType, joysprintId }),
  //     });
  //   }
  //   async getUserNotificationSettings(userId: string, token: string | null) {
  //   return this.request<ApiResponse<NotificationSettings>>(`/users/${userId}/notifications`, token, {
  //     method: 'GET',
  //   });
  // }

  // async updateUserNotificationSettings(
  //   userId: string,
  //   settings: NotificationSettings,
  //   token: string | null,
  // ) {
  //   return this.request<ApiResponse<NotificationSettings>>(`/users/${userId}/notifications`, token, {
  //     method: 'PUT',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify(settings),
  //   });
  // }
  //   /**
  //    * Send a connection request to another user
  //    */
  //   async sendConnectionRequest(params: ConnectionRequestPayload, token: string | null): Promise<ConnectionRequestResponse> {
  //     return this.request<ConnectionRequestResponse>('/users/connections/request', token, {
  //       method: 'POST',
  //       body: JSON.stringify(params),
  //     });
  //   }

  //   // Get current sprint data
  //   async getCurrentSprint(token: string) {
  //     return this.request('/joysprint/user/current-streak', token, {
  //       method: 'GET',
  //     });
  //   }

  //   // Claim reward
  //   async claimReward(rewardType: 'streak' | 'joydrop', joysprintId: string, token: string) {
  //     return this.request('/joysprint/user/claim-reward', token, {
  //       method: 'POST',
  //       body: JSON.stringify({ rewardType, joysprintId }),
  //     });
  //   }

  async withdrawConnectionRequest(connectionId: string, token: string | null): Promise<any> {
    return this.request<any>(`/users/connections/${connectionId}`, token, {
      method: 'DELETE',
    });
  }

  async respondToConnectionRequest(
    connectionId: string,
    action: 'accept' | 'reject' | 'pending',
    token: string | null
  ): Promise<any> {
    return this.request<any>('/users/connections/respond', token, {
      method: 'PATCH',
      body: JSON.stringify({ connectionId, action }),
    });
  }

  // --- COMMUNITIES API ---

  async createCommunity(
    dto: {
      name: string;
      description?: string;
      iconUrl: string;
      privacy: 'public' | 'private';
      /** Private communities only; omit to use API default */
      listedInDiscovery?: boolean;
    },
    token: string | null
  ): Promise<ApiResponse<{ id: string; name: string; description: string; iconUrl: string; privacy: string; adminId: string; memberCount: number; postCount: number }>> {
    return this.request<ApiResponse<{ id: string; name: string; description: string; iconUrl: string; privacy: string; adminId: string; memberCount: number; postCount: number }>>(
      '/communities',
      token,
      { method: 'POST', body: JSON.stringify(dto) }
    );
  }

  /**
   * Authenticated community discovery. Prefer `/communities/discover` (public + listed private).
   * Falls back to `/communities/public` when discover is not deployed (404).
   */
  async getPublicCommunities(
    params?: { search?: string; limit?: number; offset?: number; excludeUserId?: string },
    token?: string | null
  ): Promise<ApiResponse<unknown>> {
    const query = new URLSearchParams();
    if (params?.search != null) query.append('search', params.search);
    if (params?.limit != null) query.append('limit', String(params.limit));
    if (params?.offset != null) query.append('offset', String(params.offset));
    if (params?.excludeUserId != null) query.append('excludeUserId', params.excludeUserId);
    const qs = query.toString() ? `?${query.toString()}` : '';
    const discover = await this.request<ApiResponse<unknown>>(
      `/communities/discover${qs}`,
      token ?? null,
      { method: 'GET' },
      COMMUNITIES_DISCOVER_NOT_FOUND as unknown as ApiResponse<unknown>
    );
    if ((discover as unknown) === COMMUNITIES_DISCOVER_NOT_FOUND) {
      return this.request(`/communities/public${qs}`, token ?? null, { method: 'GET' });
    }
    return discover;
  }

  async getCommunity(communityId: string, token?: string | null): Promise<ApiResponse<unknown>> {
    const accessDeniedResponse: ApiResponse<unknown> = { statusCode: 403, status: 'error', data: undefined, message: 'Access denied' };
    return this.request(
      `/communities/${communityId}`,
      token ?? null,
      { method: 'GET' },
      undefined,
      accessDeniedResponse
    );
  }

  async updateCommunity(
    communityId: string,
    dto: {
      name?: string;
      description?: string;
      iconUrl?: string;
      privacy?: 'public' | 'private';
      listedInDiscovery?: boolean;
    },
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}`, token, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  }

  async deleteCommunity(communityId: string, token: string | null): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}`, token, { method: 'DELETE' });
  }

  async joinCommunity(communityId: string, token: string | null): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/members`, token, { method: 'POST' });
  }

  async getCommunityMembers(
    communityId: string,
    params?: { limit?: number; offset?: number },
    token?: string | null
  ): Promise<ApiResponse<{ members: unknown[]; total: number; hasMore: boolean }>> {
    const query = new URLSearchParams();
    if (params?.limit != null) query.append('limit', String(params.limit));
    if (params?.offset != null) query.append('offset', String(params.offset));
    const qs = query.toString() ? `?${query.toString()}` : '';
    const emptyMembersResponse: ApiResponse<{ members: unknown[]; total: number; hasMore: boolean }> = {
      statusCode: 200,
      status: 'success',
      data: { members: [], total: 0, hasMore: false },
      message: '',
    };
    return this.request(
      `/communities/${communityId}/members${qs}`,
      token ?? null,
      { method: 'GET' },
      undefined,
      emptyMembersResponse,
      emptyMembersResponse
    );
  }

  async addMember(
    communityId: string,
    userId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/members/${userId}`, token, {
      method: 'POST',
    });
  }

  /**
   * Ask the backend to create a "Welcome to [community]! Take a look!" notification for the given member.
   * Used when an admin adds a user to a public community. Backend may implement this endpoint
   * or create the notification inside addMember; if not implemented, this call may 404 and is safely ignored.
   */
  async createCommunityWelcomeNotificationForMember(
    communityId: string,
    memberUserId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    const fallbackOnNotFound: ApiResponse<unknown> = {
      statusCode: 404,
      status: 'success',
      message: 'Welcome notification endpoint unavailable; skipped',
      data: undefined,
    };
    return this.request(
      `/communities/${communityId}/members/${memberUserId}/welcome-notification`,
      token,
      { method: 'POST' },
      fallbackOnNotFound
    );
  }

  async inviteMember(
    communityId: string,
    userId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/invitations/${userId}`, token, {
      method: 'POST',
    });
  }

  async respondToCommunityInvitation(
    communityId: string,
    invitationId: string,
    action: 'accept' | 'reject',
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    const path = `/communities/${communityId}/invitations/${invitationId}/respond`;
    if (action === 'accept') {
      return this.request(path, token, {
        method: 'POST',
        body: JSON.stringify({ action: 'accept' }),
      });
    }
    try {
      return await this.request<ApiResponse<unknown>>(path, token, {
        method: 'POST',
        body: JSON.stringify({ action: 'reject' }),
      });
    } catch (rejectErr) {
      try {
        return await this.request<ApiResponse<unknown>>(path, token, {
          method: 'POST',
          body: JSON.stringify({ action: 'deny' }),
        });
      } catch {
        throw rejectErr;
      }
    }
  }

  async removeMember(
    communityId: string,
    userId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/members/${userId}`, token, { method: 'DELETE' });
  }

  async updateMemberRole(
    communityId: string,
    userId: string,
    dto: { role: 'admin' | 'member' },
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/members/${userId}/role`, token, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  }

  async getCommunityJoinRequests(
    communityId: string,
    params?: { status?: 'pending' | 'approved' | 'denied'; limit?: number; offset?: number },
    token?: string | null
  ): Promise<ApiResponse<{ data: CommunityJoinRequestItem[]; total: number; hasMore: boolean }>> {
    const query = new URLSearchParams();
    if (params?.status != null) query.append('status', params.status);
    if (params?.limit != null) query.append('limit', String(params.limit));
    if (params?.offset != null) query.append('offset', String(params.offset));
    const qs = query.toString() ? `?${query.toString()}` : '';
    const emptyJoinRequestsResponse: ApiResponse<{ data: CommunityJoinRequestItem[]; total: number; hasMore: boolean }> = {
      statusCode: 200,
      status: 'success',
      message: '',
      data: { data: [], total: 0, hasMore: false },
    };
    return this.request(
      `/communities/${communityId}/join-requests${qs}`,
      token ?? null,
      { method: 'GET' },
      undefined,
      emptyJoinRequestsResponse,
      emptyJoinRequestsResponse
    );
  }

  async approveJoinRequest(
    communityId: string,
    requestId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/join-requests/${requestId}/approve`, token, {
      method: 'POST',
    });
  }

  async denyJoinRequest(
    communityId: string,
    requestId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/join-requests/${requestId}/deny`, token, {
      method: 'POST',
    });
  }

  /** Self-service: cancel your own pending join request for a private community. */
  async withdrawJoinRequest(
    communityId: string,
    requestId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/join-requests/${requestId}`, token, {
      method: 'DELETE',
    });
  }

  /**
   * Self-service withdraw without needing to already know the join request's id — the
   * backend looks up the caller's own pending request for this community. Prefer this
   * over `withdrawJoinRequest` for UI "Withdraw Request" buttons.
   */
  async withdrawMyJoinRequest(
    communityId: string,
    token: string | null
  ): Promise<ApiResponse<unknown>> {
    return this.request(`/communities/${communityId}/join-requests/mine`, token, {
      method: 'DELETE',
    });
  }

  async getCommunityPosts(
    communityId: string,
    params?: { limit?: number; offset?: number },
    token?: string | null
  ): Promise<ApiResponse<{ posts: PostData[]; hasMore: boolean; total: number }>> {
    const query = new URLSearchParams();
    if (params?.limit != null) query.append('limit', String(params.limit));
    if (params?.offset != null) query.append('offset', String(params.offset));
    const qs = query.toString() ? `?${query.toString()}` : '';
    const emptyPostsResponse: ApiResponse<{ posts: PostData[]; hasMore: boolean; total: number }> = {
      statusCode: 200,
      status: 'success',
      message: '',
      data: { posts: [], hasMore: false, total: 0 },
    };
    return this.request(
      `/communities/${communityId}/posts${qs}`,
      token ?? null,
      { method: 'GET' },
      undefined,
      emptyPostsResponse
    );
  }

  async getUserCommunities(
    userId: string,
    params?: { role?: 'admin' | 'member'; limit?: number; offset?: number },
    token?: string | null
  ): Promise<ApiResponse<{ data: unknown[]; total: number; hasMore: boolean }>> {
    const query = new URLSearchParams();
    if (params?.role != null) query.append('role', params.role);
    if (params?.limit != null) query.append('limit', String(params.limit));
    if (params?.offset != null) query.append('offset', String(params.offset));
    const qs = query.toString() ? `?${query.toString()}` : '';
    const emptyResponse: ApiResponse<{ data: unknown[]; total: number; hasMore: boolean }> = {
      statusCode: 200,
      status: 'success',
      data: { data: [], total: 0, hasMore: false },
      message: '',
    };
    return this.request(
      `/users/${userId}/communities${qs}`,
      token ?? null,
      { method: 'GET' },
      undefined,
      emptyResponse
    );
  }

  async getUserJoinRequests(
    userId: string,
    params?: { status?: 'pending' | 'approved' | 'denied' },
    token?: string | null
  ): Promise<ApiResponse<{ data: unknown[]; total: number }>> {
    const query = new URLSearchParams();
    if (params?.status != null) query.append('status', params.status);
    const qs = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/users/${userId}/join-requests${qs}`, token ?? null, { method: 'GET' });
  }
}

export function createApiService(baseUrl: string): ApiService {
  return new ApiService(baseUrl);
}
